// packages/editor/src/studio/SlotInspector.tsx
//
// Right-rail panel (~320px) opened when the cursor is on a
// :::slot[name]{attrs}::: directive. Form-bound to a SlotConfig;
// explicit Apply button regenerates the directive string and lets the
// parent component splice it back into source. Real-time write-back
// rejected (too easy to corrupt while typing).

import { useState } from 'react';
import type { SlotConfig } from '../slot-grammar/slot-types';
import { serializeSlot } from '../slot-grammar/serialize-slot';

const KNOWN_SCHEMAS = ['Markdown', 'HeroStatsV1', 'PaymentCtaV1', 'other'];
const SOURCES = ['auto', 'human', 'slash-only'] as const;
const REVIEWERS = ['none', 'compliance', 'chef', 'admin'] as const;

export interface SlotInspectorProps {
  /** Initial value (parsed from the slot directive at the cursor). */
  initial: SlotConfig;
  /** Called when Apply is clicked with the regenerated directive string. */
  onApply: (directiveString: string, updated: SlotConfig) => void;
  /** Called when the inspector is dismissed without applying. */
  onClose: () => void;
}

export function SlotInspector({ initial, onApply, onClose }: SlotInspectorProps) {
  const [cfg, setCfg] = useState<SlotConfig>(initial);
  const [slashInput, setSlashInput] = useState('');

  const handleApply = () => {
    onApply(serializeSlot(cfg), cfg);
  };

  return (
    <div
      data-testid="slot-inspector"
      style={{
        width: 320,
        height: '100%',
        borderLeft: '1px solid #e5e7eb',
        background: '#ffffff',
        padding: 16,
        overflowY: 'auto',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 13,
      }}
    >
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <strong style={{ color: '#4a6fa5' }}>SLOT · {cfg.name || '(unnamed)'}</strong>
        <button
          data-testid="slot-close"
          onClick={onClose}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 16 }}
        >
          ×
        </button>
      </header>

      <Field label="name">
        <input
          data-testid="slot-name"
          value={cfg.name}
          onChange={(e) => setCfg({ ...cfg, name: e.target.value })}
          style={inputStyle}
        />
      </Field>

      <Field label="schema">
        <select
          data-testid="slot-schema"
          value={KNOWN_SCHEMAS.includes(cfg.schema) ? cfg.schema : 'other'}
          onChange={(e) => setCfg({ ...cfg, schema: e.target.value === 'other' ? cfg.schema : e.target.value })}
          style={inputStyle}
        >
          {KNOWN_SCHEMAS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        {!KNOWN_SCHEMAS.includes(cfg.schema) && (
          <input
            data-testid="slot-schema-other"
            value={cfg.schema}
            onChange={(e) => setCfg({ ...cfg, schema: e.target.value })}
            placeholder="custom schema name"
            style={{ ...inputStyle, marginTop: 4 }}
          />
        )}
      </Field>

      <Field label="source">
        <select
          data-testid="slot-source"
          value={cfg.source}
          onChange={(e) => setCfg({ ...cfg, source: e.target.value as SlotConfig['source'] })}
          style={inputStyle}
        >
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </Field>

      <Field label="reviewer">
        <select
          data-testid="slot-reviewer"
          value={cfg.reviewer}
          onChange={(e) => setCfg({ ...cfg, reviewer: e.target.value as SlotConfig['reviewer'] })}
          style={inputStyle}
        >
          {REVIEWERS.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </Field>

      <Field label="trigger">
        <input
          data-testid="slot-trigger"
          value={cfg.trigger ?? ''}
          onChange={(e) => setCfg({ ...cfg, trigger: e.target.value || undefined })}
          placeholder="event.type.name"
          style={inputStyle}
        />
      </Field>

      <Field label="slash">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 4 }}>
          {cfg.slash.map((id, i) => (
            <span key={i} data-testid="slot-slash-tag" style={tagStyle}>
              {id}
              <button
                onClick={() => setCfg({ ...cfg, slash: cfg.slash.filter((_, j) => j !== i) })}
                style={{ marginLeft: 4, background: 'transparent', border: 'none', cursor: 'pointer' }}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        <input
          data-testid="slot-slash-input"
          value={slashInput}
          onChange={(e) => setSlashInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && slashInput.trim()) {
              setCfg({ ...cfg, slash: [...cfg.slash, slashInput.trim()] });
              setSlashInput('');
              e.preventDefault();
            }
          }}
          placeholder="add formatter id, Enter"
          style={inputStyle}
        />
      </Field>

      <Field label="maxWords">
        <input
          data-testid="slot-maxwords"
          type="number"
          value={cfg.maxWords ?? ''}
          onChange={(e) => setCfg({ ...cfg, maxWords: e.target.value ? Number(e.target.value) : undefined })}
          style={inputStyle}
        />
      </Field>

      <Field label="default">
        <input
          data-testid="slot-default"
          value={cfg.default ?? ''}
          onChange={(e) => setCfg({ ...cfg, default: e.target.value || undefined })}
          style={inputStyle}
        />
      </Field>

      <button
        data-testid="slot-apply"
        onClick={handleApply}
        style={{
          marginTop: 12,
          padding: '8px 16px',
          background: '#4a6fa5',
          color: '#fff',
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer',
          width: '100%',
          fontSize: 13,
        }}
      >
        Apply
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, color: '#6b7280', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '4px 6px',
  border: '1px solid #d1d5db',
  borderRadius: 3,
  fontSize: 13,
  fontFamily: 'monospace',
  boxSizing: 'border-box',
};

const tagStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  background: '#eef2ff',
  color: '#4a6fa5',
  padding: '2px 6px',
  borderRadius: 3,
  fontSize: 11,
  fontFamily: 'monospace',
};
